import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Check, Scales } from "@phosphor-icons/react";

/**
 * WeighOutModal — Stage B of the truck lifecycle.
 * Enter the weighbridge tonnage. Closes the truck visit.
 */
export default function WeighOutModal({ exportOp, visit, onClose, onSuccess }) {
    const [tons, setTons] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const t = parseFloat(tons);
        if (!(t > 0)) return toast.error("Weighbridge tons must be > 0.");
        setBusy(true);
        try {
            await api.post(`/tronox/exports/${exportOp.id}/trucks/${visit.id}/weigh-out`, { tons: t });
            toast.success(`${visit.plate} weighed out at ${t} t`);
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="weigh-out-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-sky-500/20 border border-sky-500/50 flex items-center justify-center">
                            <Scales size={18} weight="fill" className="text-sky-300" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">Weighbridge · Stage {visit.status === "pending_weigh_out" ? "C" : "B"}</div>
                            <div className="text-lg font-black">Weigh Out — {visit.plate}</div>
                            {visit.departed_by_name && (
                                <div className="text-[10px] text-slate-500">Sent from load-out by {visit.departed_by_name}</div>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-weigh-out">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded bg-slate-800/60 border border-slate-700 p-2">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Plate</div>
                            <div className="font-mono font-black text-teal-300 text-sm">{visit.plate}</div>
                        </div>
                        <div className="rounded bg-slate-800/60 border border-slate-700 p-2">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Skips</div>
                            <div className="font-black text-slate-100 text-lg">{visit.skips}</div>
                        </div>
                        <div className="rounded bg-slate-800/60 border border-slate-700 p-2">
                            <div className="text-[10px] uppercase text-slate-500 tracking-wider">Silo</div>
                            <div className="font-black text-slate-100 text-lg">#{visit.silo_no}</div>
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Weighbridge tons</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={tons}
                            onChange={(e) => setTons(e.target.value)}
                            placeholder="e.g. 32.5"
                            className="g-input mt-1 text-2xl font-mono font-black text-center"
                            data-testid="weigh-out-tons-input"
                            autoFocus
                        />
                        <div className="text-[10px] text-slate-500 mt-1">
                            This is the truck&apos;s weighbridge tonnage (for fleet analytics). The <b>conveyor scale reading</b> drives the flow rate — log those via <i>Log Silo Tons</i>.
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="weigh-out-cancel">Cancel</button>
                        <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 text-white font-black" data-testid="weigh-out-submit">
                            <Check size={16} weight="bold" /> {busy ? "Saving…" : "Weigh Out"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
